
import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
}

export default function ProtectedRoute({ children, requireAuth = true }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, profile, isAuthReady } = useAuth();
  const location = useLocation();

  console.log('[ProtectedRoute] DEBUGGING - Auth check:', { 
    isAuthenticated, 
    isLoading,
    isAuthReady,
    requireAuth,
    path: location.pathname,
    hasUser: !!user,
    hasProfile: !!profile,
    userEmail: user?.email,
    profileRole: profile?.role_text,
    isSystemAccount: profile?.is_system_account,
    timestamp: new Date().toISOString()
  });

  // Wait until auth is fully ready
  if (!isAuthReady) {
    console.log('[ProtectedRoute] Auth not ready, waiting...');
    return null;
  }

  // Only show loading when authentication is genuinely unknown (no user data at all)
  if (isLoading && !user && !profile) {
    console.log('[ProtectedRoute] Auth genuinely loading, showing loading state');
    return (
      <div className="min-h-screen flex items-center justify-center app-background">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-gold" />
          <span className="text-black">Checking authentication...</span>
        </div>
      </div>
    );
  }

  // Only redirect after auth is fully resolved
  if (requireAuth && !isAuthenticated && isAuthReady) {
    console.log('[ProtectedRoute] User not authenticated, redirecting to auth', {
      path: location.pathname,
      requireAuth,
      isAuthenticated,
      isAuthReady,
      hasUser: !!user,
      isLoading
    });
    return (
      <Navigate 
        to="/auth" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  console.log('[ProtectedRoute] Access granted', {
    isAuthenticated,
    requireAuth,
    path: location.pathname,
    userType: profile?.is_system_account ? 'system' : 'regular'
  });

  return <>{children}</>;
}
